import { ReactElement } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { AdminPermission } from '@localstack/types';

import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';

import { NotFound } from '../common';

import { Accounts, Account, CreateAccount } from './Accounts';

import { Organizations, Organization, Subscribe, Subscription } from './Organizations';
import { AdminExtensions, AdminExtension } from './Extensions';
import { AdminAnnouncement } from './AdminAnnouncement';
import { Dashboard } from './Dashboard';
import { FeatureFlags } from './FeatureFlags';
import { AdminUsers } from './AdminUsers';

export const AdminViews = (): ReactElement => {
  const { isPlatformAdmin, can } = useAuthProvider();

  if (!isPlatformAdmin) {
    return <Navigate replace to={AppRoute.NOT_FOUND} />;
  }

  return (
    <Routes>
      <Route element={<NotFound />} path={AppRoute.NOT_FOUND} />
      <Route element={<Dashboard />} path={AppRoute.ADMIN_HOME} />
      {can(AdminPermission.USERS_READ) && <Route element={<Accounts />} path={AppRoute.ADMIN_ACCOUNTS} />}
      {can(AdminPermission.USERS_READ) && <Route element={<Account />} path={AppRoute.ADMIN_ACCOUNT} />}
      {can(AdminPermission.USERS_WRITE) && <Route element={<CreateAccount />} path={AppRoute.ADMIN_CREATE_ACCOUNT} />}
      {can(AdminPermission.WORKSPACES_READ) && (
        <Route element={<Organizations />} path={AppRoute.ADMIN_ORGANIZATIONS} />
      )}
      {can(AdminPermission.WORKSPACES_READ) && <Route element={<Organization />} path={AppRoute.ADMIN_ORGANIZATION} />}
      {can(AdminPermission.WORKSPACES_WRITE) && (
        <Route element={<Subscribe />} path={AppRoute.ADMIN_ORGANIZATION_SUBSCRIBE} />
      )}
      {can(AdminPermission.WORKSPACES_READ) && (
        <Route element={<Subscription />} path={AppRoute.ADMIN_ORGANIZATION_SUBSCRIPTION} />
      )}
      {can(AdminPermission.WORKSPACES_WRITE) && (
        <Route element={<Subscribe />} path={AppRoute.ADMIN_ORGANIZATION_SUBSCRIPTION_UPDATE} />
      )}
      {can(AdminPermission.EXTENSIONS_READ) && <Route element={<AdminExtensions />} path={AppRoute.ADMIN_EXTENSIONS} />}
      {can(AdminPermission.EXTENSIONS_READ) && <Route element={<AdminExtension />} path={AppRoute.ADMIN_EXTENSION} />}
      {can(AdminPermission.ANNOUNCEMENTS) && (
        <Route element={<AdminAnnouncement />} path={AppRoute.ADMIN_ANNOUNCEMENT} />
      )}
      {can(AdminPermission.FEATURE_FLAGS_READ) && (
        <Route element={<FeatureFlags />} path={AppRoute.ADMIN_FEATURE_FLAGS} />
      )}
      {can(AdminPermission.SUPER_ADMIN) && <Route element={<AdminUsers />} path={AppRoute.ADMIN_ADMIN_USERS} />}
      <Route element={<Navigate replace to={AppRoute.NOT_FOUND} />} path="*" />
    </Routes>
  );
};
